import React, { useState } from "react";
import { Button, FormGroup, Modal, TextInput, Select } from "src/components";
import Axios from "src/services/api";
import { toast } from "react-hot-toast";
import styles from "./IndiaBusinessModal.module.scss";
import supportedCountries from "src/services/supportedCountries.json";

interface IndiaBusinessData {
  indiaBusinessName: string;
  indiaBusinessAddressLine1: string;
  indiaBusinessAddressLine2: string;
  indiaBusinessAddressCity: string;
  indiaBusinessAddressState: string;
  indiaBusinessAddressPostalCode: string;
  indiaBusinessAddressCountry: string;
  indiaBusinessGSTIN: string;
  indiaBusinessPAN: string;
  indiaBusinessEmail: string;
  indiaBusinessPhone: string;
  indiaBusinessWebsite: string;
}

interface IndiaBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userData?: any;
}

const INDIAN_STATES = [
  { value: "AN", label: "Andaman and Nicobar Islands" },
  { value: "AP", label: "Andhra Pradesh" },
  { value: "AR", label: "Arunachal Pradesh" },
  { value: "AS", label: "Assam" },
  { value: "BR", label: "Bihar" },
  { value: "CH", label: "Chandigarh" },
  { value: "CT", label: "Chhattisgarh" },
  { value: "DN", label: "Dadra and Nagar Haveli and Daman and Diu" },
  { value: "DL", label: "Delhi" },
  { value: "GA", label: "Goa" },
  { value: "GJ", label: "Gujarat" },
  { value: "HR", label: "Haryana" },
  { value: "HP", label: "Himachal Pradesh" },
  { value: "JK", label: "Jammu and Kashmir" },
  { value: "JH", label: "Jharkhand" },
  { value: "KA", label: "Karnataka" },
  { value: "KL", label: "Kerala" },
  { value: "LA", label: "Ladakh" },
  { value: "LD", label: "Lakshadweep" },
  { value: "MP", label: "Madhya Pradesh" },
  { value: "MH", label: "Maharashtra" },
  { value: "MN", label: "Manipur" },
  { value: "ML", label: "Meghalaya" },
  { value: "MZ", label: "Mizoram" },
  { value: "NL", label: "Nagaland" },
  { value: "OR", label: "Odisha" },
  { value: "PY", label: "Puducherry" },
  { value: "PB", label: "Punjab" },
  { value: "RJ", label: "Rajasthan" },
  { value: "SK", label: "Sikkim" },
  { value: "TN", label: "Tamil Nadu" },
  { value: "TS", label: "Telangana" },
  { value: "TR", label: "Tripura" },
  { value: "UP", label: "Uttar Pradesh" },
  { value: "UT", label: "Uttarakhand" },
  { value: "WB", label: "West Bengal" },
];

const COUNTRY_OPTIONS = supportedCountries.map((country) => ({
  value: country.code,
  label: `${country.name}`,
}));

const IndiaBusinessModal: React.FC<IndiaBusinessModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userData,
}) => {
  const [formData, setFormData] = useState<IndiaBusinessData>({
    indiaBusinessName: userData?.indiaBusinessName || "",
    indiaBusinessAddressLine1: userData?.indiaBusinessAddressLine1 || "",
    indiaBusinessAddressLine2: userData?.indiaBusinessAddressLine2 || "",
    indiaBusinessAddressCity: userData?.indiaBusinessAddressCity || "",
    indiaBusinessAddressState: userData?.indiaBusinessAddressState || "",
    indiaBusinessAddressPostalCode:
      userData?.indiaBusinessAddressPostalCode || "",
    indiaBusinessAddressCountry: userData?.indiaBusinessAddressCountry || "",
    indiaBusinessGSTIN: userData?.indiaBusinessGSTIN || "",
    indiaBusinessPAN: userData?.indiaBusinessPAN || "",
    indiaBusinessEmail: userData?.indiaBusinessEmail || "",
    indiaBusinessPhone: userData?.indiaBusinessPhone || "",
    indiaBusinessWebsite: userData?.indiaBusinessWebsite || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (key: keyof IndiaBusinessData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.indiaBusinessName) {
      newErrors.indiaBusinessName = "Business name is required";
    }

    if (!formData.indiaBusinessAddressLine1) {
      newErrors.indiaBusinessAddressLine1 = "Business line 1 is required";
    }

    if (!formData.indiaBusinessAddressCity) {
      newErrors.indiaBusinessAddressCity = "City is required";
    }

    if (!formData.indiaBusinessAddressState) {
      newErrors.indiaBusinessAddressState = "State is required";
    }

    if (!formData.indiaBusinessAddressPostalCode) {
      newErrors.indiaBusinessAddressPostalCode = "Postal code is required";
    }

    if (!formData.indiaBusinessAddressCountry) {
      newErrors.indiaBusinessAddressCountry = "Country is required";
    }

    if (!formData.indiaBusinessGSTIN) {
      newErrors.indiaBusinessGSTIN = "GSTIN is required";
    } else if (
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.indiaBusinessGSTIN
      )
    ) {
      newErrors.indiaBusinessGSTIN = "Invalid GSTIN format";
    }

    if (!formData.indiaBusinessPAN) {
      newErrors.indiaBusinessPAN = "PAN is required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.indiaBusinessPAN)) {
      newErrors.indiaBusinessPAN = "Invalid PAN format";
    }

    if (!formData.indiaBusinessEmail) {
      newErrors.indiaBusinessEmail = "Business email is required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.indiaBusinessEmail)
    ) {
      newErrors.indiaBusinessEmail = "Invalid email format";
    }

    if (!formData.indiaBusinessPhone) {
      newErrors.indiaBusinessPhone = "Business phone is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await toast.promise(
        Axios.post("/users/request-india-business-approval", formData),
        {
          loading: "Saving business details...",
          success:
            "Business details saved successfully! We'll notify you within 24 hours.",
          error: "Failed to save business details",
        }
      );

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving business details:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Indian Business Details"
      onClose={onClose}
      footerRightButton1={{
        label: "Cancel",
        onClick: onClose,
      }}
      footerRightButton2={{
        label: isSubmitting ? "Saving..." : "Save",
        onClick: handleSubmit,
        disabled: isSubmitting,
      }}
    >
      <div className={styles.modalContent}>
        <p className={styles.infoText}>
          To comply with Indian regulations, we need additional business details
          before you can connect your bank account.
        </p>

        <FormGroup
          label="Business Name"
          error={errors.indiaBusinessName}
          required
        >
          <TextInput
            name="indiaBusinessName"
            value={formData.indiaBusinessName}
            onChange={(e) => handleChange("indiaBusinessName", e.target.value)}
            error={errors.indiaBusinessName}
            placeholder="Enter your business name"
          />
        </FormGroup>

        <FormGroup
          label="Address Line 1"
          error={errors.indiaBusinessAddressLine1}
          required
        >
          <TextInput
            name="indiaBusinessAddressLine1"
            value={formData.indiaBusinessAddressLine1}
            onChange={(e) =>
              handleChange("indiaBusinessAddressLine1", e.target.value)
            }
            error={errors.indiaBusinessAddressLine1}
            placeholder="Enter your business address"
          />
        </FormGroup>

        <FormGroup
          label="Address Line 2"
          error={errors.indiaBusinessAddressLine2}
        >
          <TextInput
            name="indiaBusinessAddressLine2"
            value={formData.indiaBusinessAddressLine2}
            onChange={(e) =>
              handleChange("indiaBusinessAddressLine2", e.target.value)
            }
            error={errors.indiaBusinessAddressLine2}
            placeholder="Enter your business address"
          />
        </FormGroup>

        <FormGroup
          label="City"
          error={errors.indiaBusinessAddressCity}
          required
        >
          <TextInput
            name="indiaBusinessAddressCity"
            value={formData.indiaBusinessAddressCity}
            onChange={(e) =>
              handleChange("indiaBusinessAddressCity", e.target.value)
            }
            error={errors.indiaBusinessAddressCity}
            placeholder="Enter your business city"
          />
        </FormGroup>

        <FormGroup
          label="State"
          error={errors.indiaBusinessAddressState}
          required
        >
          <Select
            options={INDIAN_STATES}
            selectedValue={formData.indiaBusinessAddressState}
            onChange={(value) =>
              handleChange("indiaBusinessAddressState", value)
            }
            placeholder="Select your state"
            error={errors.indiaBusinessAddressState}
          />
        </FormGroup>

        <FormGroup
          label="Postal Code"
          error={errors.indiaBusinessAddressPostalCode}
          required
        >
          <TextInput
            name="indiaBusinessAddressPostalCode"
            value={formData.indiaBusinessAddressPostalCode}
            onChange={(e) =>
              handleChange("indiaBusinessAddressPostalCode", e.target.value)
            }
            error={errors.indiaBusinessAddressPostalCode}
            placeholder="Enter your business postal code"
          />
        </FormGroup>

        <FormGroup
          label="Country"
          error={errors.indiaBusinessAddressCountry}
          required
        >
          <Select
            options={COUNTRY_OPTIONS}
            selectedValue={formData.indiaBusinessAddressCountry}
            onChange={(value) =>
              handleChange("indiaBusinessAddressCountry", value)
            }
            placeholder="Select your country"
            error={errors.indiaBusinessAddressCountry}
          />
        </FormGroup>

        <div className={styles.formGrid}>
          <FormGroup label="GSTIN" error={errors.indiaBusinessGSTIN} required>
            <TextInput
              name="indiaBusinessGSTIN"
              value={formData.indiaBusinessGSTIN}
              onChange={(e) =>
                handleChange("indiaBusinessGSTIN", e.target.value)
              }
              error={errors.indiaBusinessGSTIN}
              placeholder="e.g. 22AAAAA0000A1Z5"
            />
          </FormGroup>

          <FormGroup label="PAN" error={errors.indiaBusinessPAN} required>
            <TextInput
              name="indiaBusinessPAN"
              value={formData.indiaBusinessPAN}
              onChange={(e) => handleChange("indiaBusinessPAN", e.target.value)}
              error={errors.indiaBusinessPAN}
              placeholder="e.g. AAAAA0000A"
            />
          </FormGroup>
        </div>

        <FormGroup
          label="Business Email"
          error={errors.indiaBusinessEmail}
          required
        >
          <TextInput
            name="indiaBusinessEmail"
            value={formData.indiaBusinessEmail}
            onChange={(e) => handleChange("indiaBusinessEmail", e.target.value)}
            error={errors.indiaBusinessEmail}
            placeholder="e.g. business@example.com"
            type="email"
          />
        </FormGroup>

        <FormGroup
          label="Business Phone"
          error={errors.indiaBusinessPhone}
          required
        >
          <TextInput
            name="indiaBusinessPhone"
            value={formData.indiaBusinessPhone}
            onChange={(e) => handleChange("indiaBusinessPhone", e.target.value)}
            error={errors.indiaBusinessPhone}
            placeholder="e.g. +91 9876543210"
          />
        </FormGroup>

        <FormGroup label="Business Website (optional)">
          <TextInput
            name="indiaBusinessWebsite"
            value={formData.indiaBusinessWebsite}
            onChange={(e) =>
              handleChange("indiaBusinessWebsite", e.target.value)
            }
            placeholder="e.g. https://www.example.com"
          />
        </FormGroup>
      </div>
    </Modal>
  );
};

export default IndiaBusinessModal;
